import type { ManagedResource, ResourceInput, ResourceList } from '../../../shared/resource-catalog.mjs';
import { checkedResourceInput } from './resourceValidation';

const DATABASE = 'designforge-resource-catalog';
const STORE = 'resources';
const CHANGE = 'designforge:resources-updated';
const CAPACITY = 1000;
let connection: Promise<IDBDatabase> | undefined;

function openDatabase(): Promise<IDBDatabase> {
  if (!connection) connection = new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DATABASE, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE, { keyPath: 'id' });
    request.onsuccess = () => {
      request.result.onversionchange = () => { request.result.close(); connection = undefined; };
      resolve(request.result);
    };
    request.onerror = () => reject(new Error('Không mở được bộ nhớ tài nguyên trong trình duyệt.'));
    request.onblocked = () => reject(new Error('Hãy đóng các tab DesignForge cũ rồi thử lại.'));
  }).catch(error => { connection = undefined; throw error; });
  return connection;
}

function notifyChanges() {
  window.dispatchEvent(new Event(CHANGE));
  if (typeof BroadcastChannel !== 'undefined') {
    const channel = new BroadcastChannel(CHANGE);
    channel.postMessage('changed');
    channel.close();
  }
}

export function subscribeLocalResources(listener: () => void) {
  window.addEventListener(CHANGE, listener);
  const channel = typeof BroadcastChannel === 'undefined' ? undefined : new BroadcastChannel(CHANGE);
  if (channel) channel.onmessage = listener;
  return () => { window.removeEventListener(CHANGE, listener); channel?.close(); };
}

export async function listLocalResources(publishedOnly = false): Promise<ResourceList> {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE, 'readonly');
    const request = transaction.objectStore(STORE).getAll();
    request.onsuccess = () => {
      const records = (request.result as ManagedResource[]).filter(item => !publishedOnly || item.status === 'published').sort((a, b) => b.updatedAt.localeCompare(a.updatedAt) || a.id.localeCompare(b.id));
      resolve({ items: records.slice(0, 500), total: records.length, limit: 500 });
    };
    request.onerror = () => reject(new Error('Không đọc được tài nguyên đã lưu trên trình duyệt.'));
  });
}

function validateLocalInput(input: ResourceInput): ResourceInput {
  // Local previews can persist a thumbnail selected from disk. The live API only
  // accepts HTTPS URLs; it stores uploaded images behind its own image endpoint.
  const localImage = /^data:image\/(?:webp|png|jpeg);base64,[A-Za-z0-9+/]+={0,2}$/.test(input.thumbnailUrl);
  if (localImage && input.thumbnailUrl.length > 350000) throw new Error('Thumbnail quá lớn. Hãy chọn lại ảnh.');
  const normalized = checkedResourceInput({ ...input, thumbnailUrl: localImage ? 'https://designforge.site/local-thumbnail' : input.thumbnailUrl });
  return { ...normalized, thumbnailUrl: localImage ? input.thumbnailUrl : normalized.thumbnailUrl };
}

export async function saveLocalResource(input: ResourceInput, id?: string, version?: number): Promise<ManagedResource> {
  const value = validateLocalInput(input);
  return writeLocalResource(id, version, current => {
    const now = new Date().toISOString();
    return { ...value, id: current?.id ?? crypto.randomUUID(), version: (current?.version ?? 0) + 1, createdAt: current?.createdAt ?? now, updatedAt: now };
  });
}

export async function removeLocalResource(id: string, version: number): Promise<ManagedResource> {
  return writeLocalResource(id, version, current => {
    if (!current) throw new Error('Không tìm thấy tài nguyên.');
    return { ...current, status: 'deleted', version: current.version + 1, updatedAt: new Date().toISOString() };
  });
}

async function writeLocalResource(id: string | undefined, version: number | undefined, update: (current?: ManagedResource) => ManagedResource): Promise<ManagedResource> {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE, 'readwrite');
    const store = transaction.objectStore(STORE);
    let item: ManagedResource;
    let failure: Error | undefined;
    const apply = (current?: ManagedResource) => {
      try {
        if (id && (!current || current.version !== version)) throw new Error('Tài nguyên đã thay đổi ở phiên khác. Hãy tải lại danh sách trước khi lưu.');
        item = update(current);
        store.put(item);
      } catch (error) { failure = error instanceof Error ? error : new Error('Không lưu được tài nguyên.'); transaction.abort(); }
    };
    if (id) {
      const request = store.get(id);
      request.onsuccess = () => apply(request.result as ManagedResource | undefined);
    } else {
      const request = store.count();
      request.onsuccess = () => {
        if (request.result >= CAPACITY) { failure = new Error('Bộ nhớ local đã đạt giới hạn 1.000 tài nguyên.'); transaction.abort(); }
        else apply();
      };
    }
    transaction.oncomplete = () => { notifyChanges(); resolve(item); };
    transaction.onabort = transaction.onerror = () => reject(failure ?? new Error('Không lưu được tài nguyên trên trình duyệt.'));
  });
}
